import { ProjectsSearchBar } from '@/components/shared/ProjectsSearchBar';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { Avatar, Button } from '@/components/ui';
import type { MilestoneView, ProjectView } from '@/types/current-project';
import { weekWindowLabel } from '@/lib/project-presentation';
import { useAuthStore } from '@/stores/authStore';
import { useProjectParticipants } from '@/hooks/useProjectViews';
import { getProposalPresentation } from '@/api/adapter/proposalReviews';
import type { ProjectParticipant } from '@/api/adapter/projectParticipants';
import { profileImageUrl } from '@/hooks/useProfile';

const columns = [
  {
    label: 'Scheduled Milestones',
    statuses: [null, 'NotStarted', 'InProgress'],
    headerStyle: 'green',
  },
  {
    label: 'To Review',
    statuses: ['CompletionRequested', 'ChangesRequested', 'Disputed'],
    headerStyle: 'dark',
    accentBorder: true,
  },
  { label: 'Completed', statuses: ['Completed'], headerStyle: 'dark' },
];
export function TasksKanbanView({
  projects,
  onNavigate,
  onMilestoneClick,
}: {
  projects: ProjectView[];
  onNavigate: (id: string) => void;
  onMilestoneClick?: (milestone: MilestoneView) => void;
}) {
  const [search, setSearch] = useState('');
  const [selection, setSelection] = useState(projects[0]?.projectId ?? '');
  const selected = projects.some((p) => p.projectId === selection)
    ? selection
    : projects[0]?.projectId;
  const project = projects.find((p) => p.projectId === selected);
  const allMilestones = projects.flatMap((p) =>
    p.proposals
      .filter((q) => q.status === 'Approved')
      .flatMap((q) => q.milestones)
  );
  return (
    <div>
      <ProjectsSearchBar
        projects={projects}
        selectedProjectId={selected ?? ''}
        searchQuery={search}
        onProjectChange={setSelection}
        onSearchChange={setSearch}
        searchLabel="Search milestones"
      />
      {project && (
        <MilestoneBoard
          key={project.projectId}
          project={project}
          total={allMilestones.length}
          search={search}
          onNavigate={onNavigate}
          onMilestoneClick={onMilestoneClick}
        />
      )}
    </div>
  );
}
function MilestoneBoard({
  project,
  total,
  search,
  onNavigate,
  onMilestoneClick,
}: {
  project: ProjectView;
  total: number;
  search: string;
  onNavigate: (id: string) => void;
  onMilestoneClick?: (milestone: MilestoneView) => void;
}) {
  const principal = useAuthStore((s) => s.session?.principalId);
  const participants = useProjectParticipants(project.projectId);
  const proposals = project.proposals.filter((p) => p.status === 'Approved');
  const presentations = useQueries({
    queries: proposals.map((proposal) => ({
      queryKey: [
        'proposal-presentation',
        principal,
        project.projectId,
        proposal.proposalId,
      ],
      enabled: !!principal,
      retry: false as const,
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const result = await getProposalPresentation(
          project.projectId,
          proposal.proposalId,
          signal
        );
        if (useAuthStore.getState().session?.principalId !== principal)
          throw new Error('Your account changed. Reload proposal details.');
        return result;
      },
    })),
  });
  const milestones = proposals
    .flatMap((proposal, index) => {
      const details = presentations[index]?.data;
      return proposal.milestones.map((milestone) => ({
        milestone,
        description: details?.matchesCurrentDefinition
          ? details.milestones.find((m) => m.key === milestone.definition.key)
              ?.description
          : undefined,
      }));
    })
    .filter(({ milestone, description }) =>
      (milestone.definition.title + ' ' + (description ?? ''))
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  const failed = participants.isError || presentations.some((q) => q.isError);
  return (
    <div>
      <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
        {columns.map((column) => {
          const items = milestones.filter(({ milestone }) =>
            column.statuses.some((status) => status === milestone.status)
          );
          const green = column.headerStyle === 'green';
          return (
            <section
              key={column.label}
              aria-label={column.label}
              className={
                'flex flex-col gap-3 rounded-[--radi-6] bg-[--base-fill-1] px-3 py-4 ' +
                (column.accentBorder
                  ? 'border border-[--state-brand-active]'
                  : '')
              }
            >
              <h2
                className={
                  'flex h-10 w-full shrink-0 items-center gap-2 rounded-[--radi-6] px-3 text-lg font-semibold shadow-elevation-sm ' +
                  (green
                    ? 'bg-[--state-brand-active] text-[--text-light-primary]'
                    : 'border border-[--base-border] bg-[--base-surface-2] text-[--text-dark-primary]')
                }
              >
                <span
                  className={
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-sm font-semibold ' +
                    (green
                      ? 'bg-black/15'
                      : 'bg-[--base-fill-2] text-[--text-dark-secondary]')
                  }
                >
                  {items.length}
                </span>{' '}
                {column.label}
              </h2>
              {!items.length && (
                <p className="py-8 text-center text-xs text-[--text-dark-secondary]">
                  No milestones
                </p>
              )}
              {items.map(({ description, milestone }) => (
                <MilestoneCard
                  key={milestone.milestoneId}
                  project={project}
                  description={description}
                  workers={participants.data?.workers ?? []}
                  milestone={milestone}
                  onClick={() =>
                    onMilestoneClick
                      ? onMilestoneClick(milestone)
                      : onNavigate(project.projectId)
                  }
                />
              ))}
            </section>
          );
        })}
      </div>
      {!!total && (
        <p className="mt-4 text-center text-xs text-[--text-dark-secondary]">
          {`Showing ${milestones.length} of ${total} milestones`}
        </p>
      )}
      {failed && (
        <p role="alert" className="mt-2 text-xs text-red-400">
          Some milestone details could not be loaded.{' '}
          <button
            className="underline"
            onClick={() => {
              void participants.refetch();
              for (const query of presentations) void query.refetch();
            }}
          >
            Try Again
          </button>
        </p>
      )}
    </div>
  );
}
function MilestoneCard({
  project,
  description,
  workers,
  milestone,
  onClick,
}: {
  project: ProjectView;
  description?: string;
  workers: ProjectParticipant[];
  milestone: MilestoneView;
  onClick: () => void;
}) {
  const navigate = useNavigate();
  const account = useAuthStore((s) => s.session?.accountId);
  const assigned = [...new Set(milestone.assignments.map((a) => a.worker))];
  const submit =
    account === project.coordinator &&
    !project.cancelled &&
    !project.completed &&
    !project.activeDisputeId &&
    !milestone.frozen &&
    (milestone.status === 'InProgress' ||
      milestone.status === 'ChangesRequested');
  const review =
    account === project.client && milestone.status === 'CompletionRequested';
  return (
    <article
      onClick={(event) => {
        if (!(event.target as Element).closest('button')) onClick();
      }}
      className="cursor-pointer rounded-[--radi-6] border border-[--base-border] bg-[--base-surface-1] p-3 text-left shadow-elevation-sm transition-colors hover:border-[--state-brand-active]"
    >
      <button
        type="button"
        aria-label={`Open project: ${project.title} — ${milestone.definition.title}`}
        onClick={onClick}
        className="block w-full text-left"
      >
        <div className="flex flex-col gap-2">
          <h3 className="text-lg font-semibold leading-7">
            {milestone.definition.title}
          </h3>
          {description && (
            <p className="line-clamp-2 text-base leading-6 text-[--text-dark-secondary]">
              {description}
            </p>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="inline-flex h-6 items-center rounded-xl border border-[rgba(54,211,153,0.2)] bg-[rgba(54,211,153,0.12)] px-2.5 text-xs font-medium text-[--state-brand-active]">
            {project.title}
          </span>
          {milestone.status === 'ChangesRequested' && (
            <span className="rounded-xl border border-red-400/20 bg-red-400/10 px-2.5 text-xs text-red-400">
              Rejected
            </span>
          )}
          {(milestone.frozen ||
            milestone.status === 'Disputed' ||
            project.cancelled) && (
            <span className="text-xs text-[--text-dark-secondary]">
              {project.cancelled
                ? 'Project cancelled'
                : milestone.status === 'Disputed'
                  ? 'Dispute open'
                  : 'Frozen'}
            </span>
          )}
        </div>
      </button>
      <div className="mt-4 flex min-h-9 items-center justify-between gap-2">
        <div
          className="flex min-w-0 flex-1 flex-wrap items-center gap-3"
          aria-label="Assigned developers"
        >
          {assigned.map((id) => {
            const worker = workers.find((p) => p.accountId === id);
            const name =
              worker?.profiles?.worker?.name ?? worker?.displayName ?? id;
            return (
              <div key={id} className="flex min-w-0 items-center gap-2">
                <Avatar
                  size="sm"
                  alt={name}
                  src={
                    worker?.workerImage && worker.profiles
                      ? profileImageUrl(worker.profiles.principalId, 'worker')
                      : undefined
                  }
                />
                <span className="truncate text-xs font-medium">{name}</span>
              </div>
            );
          })}
        </div>
        <div className="flex shrink-0 items-center gap-[10px]">
          <span className="inline-flex h-6 items-center rounded-xl border border-[--base-border] bg-[--base-surface-2] px-2.5 text-xs">
            {weekWindowLabel(milestone.definition.window)}
          </span>
          {submit && (
            <Button className="h-9 px-3 text-sm" onClick={onClick}>
              Submit for Review
            </Button>
          )}
          {review && (
            <Button
              className="h-9 px-3 text-sm"
              onClick={() =>
                navigate(
                  '/projects/' +
                    project.projectId +
                    '/milestones/' +
                    milestone.milestoneId +
                    '/review'
                )
              }
            >
              Review Now
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}
